import React, { useEffect, useRef, useState } from 'react';
import { InternetPlan } from '../types';

type MobilePlanCarouselProps = {
  plans: InternetPlan[];
  variant: 'standard' | 'wave' | 'business';
  getDownloadSpeed: (plan: InternetPlan) => string;
  getUploadSpeed: (plan: InternetPlan) => string;
  onGetConnected: (plan: InternetPlan) => void;
};

const MobilePlanCarousel: React.FC<MobilePlanCarouselProps> = ({
  plans,
  variant,
  getDownloadSpeed,
  getUploadSpeed,
  onGetConnected,
}) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const onScroll = () => {
      const card = el.querySelector<HTMLElement>('[data-plan-slide]');
      if (!card) return;
      const gap = 16;
      const step = card.offsetWidth + gap;
      const idx = Math.round(el.scrollLeft / step);
      setActive(Math.max(0, Math.min(plans.length - 1, idx)));
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, [plans.length]);

  const scrollTo = (index: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>('[data-plan-slide]');
    if (!card) return;
    const gap = 16;
    el.scrollTo({ left: index * (card.offsetWidth + gap), behavior: 'smooth' });
  };

  if (!plans.length) return null;

  const isStandard = variant === 'standard';
  const isBusiness = variant === 'business';
  const badgeClass = isStandard
    ? 'bg-blue-600 text-white'
    : isBusiness
      ? 'bg-white text-slate-900'
      : 'bg-green-600 text-white';
  const badgeLabel = isStandard
    ? 'STANDARD COVERAGE'
    : isBusiness
      ? 'BUSINESS WAVE'
      : 'WAVE COVERAGE';
  const subtitle = isStandard
    ? 'Fixed wireless · most addresses'
    : isBusiness
      ? 'WAVE Fiber Speeds · business'
      : 'WAVE Fiber Speeds · select areas';
  const priceClass = isBusiness ? 'text-white' : 'text-blue-400';
  const downIconBg = isBusiness ? 'bg-white/10' : 'bg-blue-500/15';
  const downIconColor = isBusiness ? 'text-white' : 'text-blue-300';
  const upIconBg = isBusiness ? 'bg-white/10' : 'bg-green-500/15';
  const upIconColor = isBusiness ? 'text-white' : 'text-green-400';
  const ctaClass = isBusiness
    ? 'bg-white text-slate-900'
    : 'bg-blue-600 text-white';

  const formatPrice = (price: number) =>
    Number.isInteger(price) ? price.toFixed(0) : price.toFixed(2);

  return (
    <div className="md:hidden">
      <div
        ref={scrollerRef}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4 scrollbar-hide"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {plans.map((plan) => (
          <article
            key={plan.id}
            data-plan-slide
            className="snap-center shrink-0 w-[min(86vw,340px)] bg-[#0a0f1a] rounded-2xl border border-white/10 shadow-lg shadow-black/30"
          >
            <div className="p-6 text-center">
              <span
                className={`inline-block px-3 py-1 rounded-full text-xs font-bold tracking-wide ${badgeClass}`}
              >
                {badgeLabel}
              </span>
              <p className="text-xs text-white/45 italic mt-2">{subtitle}</p>

              <h3 className="text-2xl font-bold text-white mt-4 mb-2 uppercase tracking-wide">
                {plan.name}
              </h3>

              <div className={`text-4xl font-bold ${priceClass}`}>
                ${formatPrice(plan.price)}
                <span className="text-lg text-white/50 font-semibold">/month</span>
              </div>
              {plan.taxesApply ? (
                <p className="text-sm text-white/45 mt-1">+ tax</p>
              ) : null}

              <div className="mt-5 flex items-center justify-center gap-8">
                <div className="text-center">
                  <div
                    className={`w-10 h-10 rounded-full ${downIconBg} flex items-center justify-center mx-auto mb-1`}
                  >
                    <svg
                      className={`w-5 h-5 ${downIconColor}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 14l-7 7m0 0l-7-7m7 7V3"
                      />
                    </svg>
                  </div>
                  <p className="text-xs text-white/45">Download</p>
                  <p className={`text-base font-bold ${downIconColor}`}>
                    {getDownloadSpeed(plan)}
                  </p>
                </div>
                <div className="text-center">
                  <div
                    className={`w-10 h-10 rounded-full ${upIconBg} flex items-center justify-center mx-auto mb-1`}
                  >
                    <svg
                      className={`w-5 h-5 ${upIconColor}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 10l7-7m0 0l7 7m-7-7v18"
                      />
                    </svg>
                  </div>
                  <p className="text-xs text-white/45">Upload</p>
                  <p className={`text-base font-bold ${upIconColor}`}>
                    {getUploadSpeed(plan)}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => onGetConnected(plan)}
                className={`mt-6 w-full py-3 px-4 rounded-lg font-semibold ${ctaClass}`}
              >
                Get Connected
              </button>
            </div>
          </article>
        ))}
      </div>

      {plans.length > 1 ? (
        <div className="mt-4 flex items-center justify-center gap-2">
          {plans.map((plan, index) => (
            <button
              key={plan.id}
              type="button"
              aria-label={`Go to plan ${index + 1}`}
              onClick={() => scrollTo(index)}
              className={`min-w-[2.25rem] h-9 px-2 rounded-full text-sm font-bold transition-all ${
                active === index
                  ? isStandard
                    ? 'bg-blue-600 text-white'
                    : 'bg-green-600 text-white'
                  : 'bg-white/10 text-white/55'
              }`}
            >
              #{index + 1}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default MobilePlanCarousel;
